import { CustomLoader } from "@admin/components/ui/custom-loader";
import { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Building,
  MapPin,
  Phone,
  Mail,
  Globe,
  Save,
  X,
  Edit3,
  CheckCircle2,
  Clock,
  XCircle,
  Calendar,
  Shield,
  Check,
  ArrowLeft,
  ArrowRight,
  Upload,
  CheckCircle,
  Play,
  Eye,
} from "lucide-react";
import { Dialog, DialogContent } from "../../components/ui/dialog";
import { useAuth } from "../../contexts/AuthContext";
import { usePrefixedNavigate } from "../../hooks/usePrefixedNavigate";
import { toast } from "sonner";
import {
  facilitiesMasterAPI,
  institutionsAPI,
  statesAPI,
  designationMasterAPI,
  type DesignationMaster,
} from "../../services/api";
import { formatDate } from "../../lib/utils";
import {
  INSTITUTION_ONBOARDING_STEPS,
  BOARDS,
  MEDIUM_OF_INSTRUCTION,
  INSTITUTION_TYPES,
  INSTITUTION_LEVELS,
  BOARDING_GENDER,
} from "../../constants/institutionFormFields";
import { Textarea } from "../../components/ui/textarea";

interface Institution {
  id: string;
  name: string;
  type: string | null;
  state_main?: string | null;
  district_main?: string | null;
  city_main?: string | null;
  area_main?: string | null;
  state_main_name?: string | null;
  district_main_name?: string | null;
  city_main_name?: string | null;
  state_name?: string | null;
  district_name?: string | null;
  city_name?: string | null;
  city: string | null;
  state: string | null;
  district?: string | null;
  designation?: string | null;
  pincode: string | null;
  address_line1: string | null;
  address_line2: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  contact_person?: string | null;
  affiliation_status?: string | null;
  year_established?: number | null;
  total_students: number | null;
  total_students_in_school?: number | null;
  total_classroom_in_school?: number | null;
  total_students_in_hostel?: number | null;
  total_rooms_in_hostel?: number | null;
  staff_count: number | null;
  teaching_staff?: number | null;
  non_teaching_staff?: number | null;
  classrooms?: number | null;
  campus_area?: number | null;
  departments: string[] | null;
  selected_departments?: string[] | null;
  facilities?: string[] | null;
  selected_facilities?: string[] | null;
  selected_levels?: string[] | null;
  trustee_name: string | null;
  trustee_contact: string | null;
  trustee_email?: string | null;
  trustee_designation?: string | null;
  trustee_count?: number | null;
  trust_registration?: string | null;
  other_trustees?: string | null;
  comments?: string | null;
  status: string | null;
  created_at: string | null;
  board?: string | null;
  medium?: string | string[] | null;
  accreditation?: string | null;
  affiliation_number?: string | null;
  recognition_number?: string | null;
  admission_link?: string | null;
  brochure?: string | null;
  /** Direct API field for image set; same key used for upload (image) */
  image?: string[] | { url: string }[] | null;
  boarding_gender?: string | null;
  educational_year?: Array<{
    educational_year: string;
    total_capacity: number;
    vacant: number;
    occupied: number;
  }> | null;
}

/** Same step titles as institution onboarding form */
const STEPS = INSTITUTION_ONBOARDING_STEPS.map(s => s.title);

const MAX_INSTITUTION_IMAGES = 15;

type FacilityOption = {
  id: string;
  name: string;
};

type LocationOption = {
  id: string;
  name: string;
};

type NamedEntity = { id: string | number; name: string };

const matchByIdOrName = <T extends NamedEntity>(
  items: T[],
  raw: string
): T | undefined => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return undefined;
  }
  const lowered = trimmed.toLowerCase();
  return items.find(
    item =>
      String(item.id) === trimmed || item.name.trim().toLowerCase() === lowered
  );
};

const toLocationOption = (
  raw: string,
  nameFallback?: string | null,
  matched?: NamedEntity
): LocationOption | null => {
  if (!raw && !nameFallback?.trim()) {
    return null;
  }
  if (matched) {
    return { id: String(matched.id), name: matched.name };
  }
  let displayName = raw;
  const trimmed = nameFallback?.trim();
  if (trimmed) {
    displayName = trimmed;
  }
  return displayName ? { id: raw, name: displayName } : null;
};

export default function Institutions() {
  const [institution, setInstitution] = useState<Institution | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<Partial<Institution>>({});
  const [newImageFiles, setNewImageFiles] = useState<File[]>([]);
  const [removedImageIndices, setRemovedImageIndices] = useState<Set<number>>(
    new Set()
  );
  const [facilityOptions, setFacilityOptions] = useState<FacilityOption[]>([]);
  const [loadingFacilities, setLoadingFacilities] = useState(false);
  const [mainStateOption, setMainStateOption] = useState<LocationOption | null>(
    null
  );
  const [mainDistrictOption, setMainDistrictOption] =
    useState<LocationOption | null>(null);
  const [mainCityOption, setMainCityOption] = useState<LocationOption | null>(
    null
  );
  const [stateOption, setStateOption] = useState<LocationOption | null>(null);
  const [districtOption, setDistrictOption] = useState<LocationOption | null>(
    null
  );
  const [cityOption, setCityOption] = useState<LocationOption | null>(null);
  const [designationOptions, setDesignationOptions] = useState<
    DesignationMaster[]
  >([]);
  const [loadingDesignations, setLoadingDesignations] = useState(false);
  // const [ setMainAreaOption] = useState<LocationOption | null>(
  //   null
  // );
  // const [educationalYearDraft, setEducationalYearDraft] = useState({
  //   educational_year: "",
  //   total_capacity: "",
  //   vacant: "",
  //   occupied: "",
  // });
  const { user } = useAuth();
  const navigate = usePrefixedNavigate();

  // Media Lightbox State
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<{
    url: string;
    type: "image" | "video";
  } | null>(null);

  const isMediaVideo = (url: string) => {
    if (!url) {
      return false;
    }
    const videoExtensions = [".mp4", ".webm", ".ogg", ".mov", ".m4v"];
    const lowercaseUrl = url.toLowerCase();
    return (
      videoExtensions.some(ext => lowercaseUrl.endsWith(ext)) ||
      url.toLowerCase().includes("video")
    );
  };

  const handleMediaClick = (url: string) => {
    setSelectedMedia({
      url,
      type: isMediaVideo(url) ? "video" : "image",
    });
    setLightboxOpen(true);
  };

  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(s => s + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(s => s - 1);
    }
  };

  const fetchInstitution = useCallback(async () => {
    if (!user) {
      return;
    }
    try {
      const raw = await institutionsAPI.getAll();
      // API returns wrapped { message, status_code, data } where data is object or array
      let list: Institution[] = [];
      if (Array.isArray(raw)) {
        list = raw as Institution[];
      } else if (raw && typeof raw === "object") {
        const inner = (raw as { data?: Institution | Institution[] }).data;
        if (Array.isArray(inner)) {
          list = inner as Institution[];
        } else if (inner && typeof inner === "object") {
          list = [inner as Institution];
        } else {
          list = ((raw as { results?: Institution[] }).results ??
            []) as Institution[];
        }
      }
      const validStr = (v: unknown): v is string =>
        typeof v === "string" && v.trim().length > 0;
      // Use image from API directly; strip empty/invalid entries from multi-select arrays
      const normalized = list.map(
        (
          item: Institution & {
            institution_images?: unknown;
            approval_status?: string;
            educational_year?: unknown;
          }
        ) => {
          let educational_yearItem = item.educational_year;
          if (typeof educational_yearItem === "string") {
            try {
              const parsed = JSON.parse(educational_yearItem);
              educational_yearItem = Array.isArray(parsed) ? parsed : null;
            } catch {
              educational_yearItem = null;
            }
          }
          if (!Array.isArray(educational_yearItem)) {
            educational_yearItem = null;
          }
          return {
            ...item,
            status: item.status ?? item.approval_status ?? null,
            image:
              item.image ??
              (item as any).institution_images ??
              (item as any).images ??
              null,
            educational_year: educational_yearItem,
            selected_levels: (item.selected_levels ?? []).filter(
              validStr
            ) as string[],
            selected_departments: (
              item.selected_departments ??
              (item as any).departments ??
              []
            ).filter(validStr) as string[],
            selected_facilities: (
              item.selected_facilities ??
              (item as any).facilities ??
              []
            ).filter(validStr) as string[],
          };
        }
      );
      setInstitution(normalized[0] ?? null);
    } catch (error: any) {
      console.error("Error fetching institution:", error);
      const isNotFound =
        error?.response?.status === 404 ||
        error?.response?.data?.message?.toLowerCase().includes("not found");
      if (!isNotFound) {
        toast.error("Failed to load institution details");
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchInstitution();
    }
  }, [user, fetchInstitution]);

  useEffect(() => {
    if (institution) {
      setFormData(institution);
    }
  }, [institution]);

  useEffect(() => {
    let mounted = true;
    const loadFacilities = async () => {
      setLoadingFacilities(true);
      try {
        const list = await facilitiesMasterAPI.list();
        if (!mounted) {
          return;
        }
        setFacilityOptions(
          list
            .filter(
              item => item.is_active !== false && String(item.name ?? "").trim()
            )
            .sort((a, b) => {
              const orderA = a.order_by ?? Number.MAX_SAFE_INTEGER;
              const orderB = b.order_by ?? Number.MAX_SAFE_INTEGER;
              if (orderA !== orderB) {
                return orderA - orderB;
              }
              return String(a.name).localeCompare(String(b.name));
            })
            .map(item => ({
              id: String(item.id),
              name: String(item.name).trim(),
            }))
        );
      } catch {
        if (!mounted) {
          return;
        }
        setFacilityOptions([]);
      } finally {
        if (mounted) {
          setLoadingFacilities(false);
        }
      }
    };

    loadFacilities();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadDesignations = async () => {
      setLoadingDesignations(true);
      try {
        const list = await designationMasterAPI.list();
        if (mounted) {
          setDesignationOptions(list.filter(d => d.is_active !== false));
        }
      } catch {
        if (mounted) {
          setDesignationOptions([]);
        }
      } finally {
        if (mounted) {
          setLoadingDesignations(false);
        }
      }
    };
    void loadDesignations();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const resolveLocationLabels = async () => {
      const stateMainRaw = String(institution?.state_main ?? "").trim();
      const districtMainRaw = String(institution?.district_main ?? "").trim();
      const cityMainRaw = String(institution?.city_main ?? "").trim();

      const stateRaw = String(institution?.state ?? "").trim();
      const districtRaw = String(institution?.district ?? "").trim();
      const cityRaw = String(institution?.city ?? "").trim();

      if (
        !stateMainRaw &&
        !districtMainRaw &&
        !cityMainRaw &&
        !stateRaw &&
        !districtRaw &&
        !cityRaw &&
        !institution?.state_main_name &&
        !institution?.district_main_name &&
        !institution?.city_main_name &&
        !institution?.state_name &&
        !institution?.district_name &&
        !institution?.city_name
      ) {
        setMainStateOption(null);
        setMainDistrictOption(null);
        setMainCityOption(null);
        setStateOption(null);
        setDistrictOption(null);
        setCityOption(null);
        return;
      }

      try {
        const states = await statesAPI.list();
        if (!mounted) {
          return;
        }

        const matchedMainState = stateMainRaw
          ? matchByIdOrName(states, stateMainRaw)
          : undefined;
        const matchedAddressState = stateRaw
          ? matchByIdOrName(states, stateRaw)
          : undefined;

        setMainStateOption(
          toLocationOption(
            stateMainRaw,
            institution?.state_main_name,
            matchedMainState
          )
        );
        setMainDistrictOption(
          toLocationOption(districtMainRaw, institution?.district_main_name)
        );
        setMainCityOption(
          toLocationOption(cityMainRaw, institution?.city_main_name)
        );
        setStateOption(
          toLocationOption(
            stateRaw,
            institution?.state_name,
            matchedAddressState
          )
        );
        setDistrictOption(
          toLocationOption(districtRaw, institution?.district_name)
        );
        setCityOption(toLocationOption(cityRaw, institution?.city_name));
      } catch {
        if (!mounted) {
          return;
        }
        setMainStateOption(
          toLocationOption(
            stateMainRaw,
            institution?.state_main_name ?? stateMainRaw
          )
        );
        setMainDistrictOption(
          toLocationOption(
            districtMainRaw,
            institution?.district_main_name ?? districtMainRaw
          )
        );
        setMainCityOption(
          toLocationOption(
            cityMainRaw,
            institution?.city_main_name ?? cityMainRaw
          )
        );
        setStateOption(
          toLocationOption(stateRaw, institution?.state_name ?? stateRaw)
        );
        setDistrictOption(
          toLocationOption(
            districtRaw,
            institution?.district_name ?? districtRaw
          )
        );
        setCityOption(
          toLocationOption(cityRaw, institution?.city_name ?? cityRaw)
        );
      }
    };

    void resolveLocationLabels();
    return () => {
      mounted = false;
    };
  }, [institution]);

  const handleSave = async () => {
    if (!institution?.id) {
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        type: formData.type ?? null,
        email: formData.email ?? null,
        phone: formData.phone ?? null,
        website: formData.website ?? null,
        contact_person: formData.contact_person ?? null,
        designation: formData.designation ?? null,
        state_main: formData.state_main ?? null,
        district_main: formData.district_main ?? null,
        city_main: formData.city_main ?? null,
        area_main: formData.area_main ?? null,
        affiliation_status: formData.affiliation_status ?? null,
        year_established: formData.year_established ?? null,
        address_line1: formData.address_line1 ?? null,
        address_line2: formData.address_line2 ?? null,
        city: formData.city ?? null,
        district: formData.district ?? null,
        state: formData.state ?? null,
        pincode: formData.pincode ?? null,
        board: formData.board ?? null,
        medium: formData.medium ?? null,
        accreditation: formData.accreditation ?? null,
        affiliation_number: formData.affiliation_number ?? null,
        recognition_number: formData.recognition_number ?? null,
        selected_levels:
          (formData.selected_levels ?? []).filter(
            (l): l is string => typeof l === "string" && l.trim().length > 0
          ) || null,
        total_students: formData.total_students ?? null,
        total_students_in_school: formData.total_students_in_school ?? null,
        total_classroom_in_school: formData.total_classroom_in_school ?? null,
        total_students_in_hostel: formData.total_students_in_hostel ?? null,
        total_rooms_in_hostel: formData.total_rooms_in_hostel ?? null,
        teaching_staff: formData.teaching_staff ?? null,
        non_teaching_staff: formData.non_teaching_staff ?? null,
        classrooms: formData.classrooms ?? null,
        campus_area: formData.campus_area ?? null,
        selected_departments:
          (formData.selected_departments ?? formData.departments ?? []).filter(
            (d): d is string => typeof d === "string" && d.trim().length > 0
          ) || null,
        selected_facilities:
          (formData.selected_facilities ?? formData.facilities ?? []).filter(
            (f): f is string => typeof f === "string" && f.trim().length > 0
          ) || null,
        trustee_name: formData.trustee_name ?? null,
        trustee_contact: formData.trustee_contact ?? null,
        trustee_email: formData.trustee_email ?? null,
        trustee_designation: formData.trustee_designation ?? null,
        trustee_count: formData.trustee_count ?? null,
        trust_registration: formData.trust_registration ?? null,
        other_trustees: formData.other_trustees ?? null,
        comments: formData.comments ?? null,
        boarding_gender: formData.boarding_gender ?? null,
        educational_year: formData.educational_year ?? null,
      };
      const payloadWithMeta = {
        ...payload,
        ...(removedImageIndices.size > 0 && {
          remove_image_indices: Array.from(removedImageIndices),
        }),
      };
      if (newImageFiles.length > 0) {
        await institutionsAPI.updateWithImages(
          institution.id,
          payloadWithMeta as Parameters<typeof institutionsAPI.update>[1] &
            Record<string, unknown>,
          newImageFiles
        );
      } else {
        await institutionsAPI.update(
          institution.id,
          payloadWithMeta as Parameters<typeof institutionsAPI.update>[1]
        );
      }

      toast.success("Institution details updated successfully");
      setIsEditing(false);
      setNewImageFiles([]);
      setRemovedImageIndices(new Set());
      fetchInstitution();
    } catch (error) {
      console.error("Error updating institution:", error);
      toast.error("Failed to update institution details");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData(institution ?? {});
    setIsEditing(false);
    setNewImageFiles([]);
    setRemovedImageIndices(new Set());
  };

  const updateField = (
    field: keyof Institution,
    value: string | number | string[] | null | Institution["educational_year"]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const normalizeFacilityIds = (raw: unknown[]): string[] =>
    Array.from(
      new Set(
        raw
          .filter((value): value is string => typeof value === "string")
          .map(value => value.trim())
          .filter(Boolean)
          .map(value => {
            const matchedById = facilityOptions.find(item => item.id === value);
            if (matchedById) {
              return matchedById.id;
            }
            const matchedByName = facilityOptions.find(
              item => item.name === value
            );
            return matchedByName?.id ?? value;
          })
      )
    );

  const getFacilityLabel = (value: string) =>
    facilityOptions.find(item => item.id === value || item.name === value)
      ?.name ?? value;

  const getDesignationLabel = (value: string | undefined | null) => {
    if (!value) {
      return "Not provided";
    }
    const match = designationOptions.find(
      d => String(d.id) === String(value) || d.name === value
    );
    return match?.name ?? value;
  };

  const getMediumLabels = (value: Institution["medium"]) => {
    const values = Array.isArray(value)
      ? value
      : typeof value === "string" && value
        ? value.split("/").filter(Boolean)
        : [];

    return values.map(
      item =>
        MEDIUM_OF_INSTRUCTION.find(option => option.value === item)?.label ??
        item
    );
  };

  const toggleLevel = (levelValue: string) => {
    const raw = formData.selected_levels ?? institution?.selected_levels ?? [];
    const current = raw.filter(
      (l): l is string => typeof l === "string" && l.trim().length > 0
    );
    const next = current.includes(levelValue)
      ? current.filter((l: string) => l !== levelValue)
      : [...current, levelValue];
    updateField("selected_levels", next);
  };

  const toggleFacility = (facilityId: string) => {
    const raw =
      formData.selected_facilities ??
      institution?.selected_facilities ??
      institution?.facilities ??
      [];
    const current = normalizeFacilityIds(raw);
    const next = current.includes(facilityId)
      ? current.filter((f: string) => f !== facilityId)
      : [...current, facilityId];
    updateField("selected_facilities", next);
  };

  const getStatusConfig = (status: string | null) => {
    switch (status) {
      case "approved":
        return {
          icon: CheckCircle2,
          label: "Approved",
          className: "bg-emerald-50 text-emerald-700 border-emerald-200",
          iconClass: "text-emerald-600",
        };
      case "pending":
        return {
          icon: Clock,
          label: "Pending Review",
          className: "bg-amber-50 text-amber-700 border-amber-200",
          iconClass: "text-amber-600",
        };
      case "rejected":
        return {
          icon: XCircle,
          label: "Rejected",
          className: "bg-red-50 text-red-700 border-red-200",
          iconClass: "text-red-600",
        };
      default:
        return {
          icon: Clock,
          label: "Unknown",
          className: "bg-gray-50 text-gray-700 border-gray-200",
          iconClass: "text-gray-600",
        };
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="space-y-4 text-center">
          <CustomLoader size="lg" />
          <p className="text-muted-foreground">
            Loading institution details...
          </p>
        </div>
      </div>
    );
  }

  if (!institution) {
    return (
      <div className="mx-auto max-w-2xl space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">My Institution</h1>
          <p className="text-muted-foreground">
            View and manage your institution details
          </p>
        </div>
        <Card className="border-dashed">
          <CardContent className="py-16 text-center">
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-primary/10">
              <Building className="h-10 w-10 text-primary" />
            </div>
            <h3 className="mb-2 text-xl font-semibold">
              No Institution Registered
            </h3>
            <p className="mx-auto mb-6 max-w-sm text-muted-foreground">
              You haven&apos;t registered an institution yet. Complete the
              onboarding process to get started.
            </p>
            <Button
              onClick={() => navigate("/institution/onboarding")}
              size="lg"
            >
              Register Your Institution
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusConfig = getStatusConfig(institution.status);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">My Institution</h1>
          <p className="text-muted-foreground">
            View and manage your institution details
          </p>
        </div>
        <div className="flex gap-3">
          {isEditing ? (
            <>
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={saving}
              >
                <X className="mr-2 h-4 w-4" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <CustomLoader size="xs" className="mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Changes
                  </>
                )}
              </Button>
            </>
          ) : (
            (institution.status === "approved" ||
              institution.status === "rejected") && (
              <Button onClick={() => setIsEditing(true)} variant="outline">
                <Edit3 className="mr-2 h-4 w-4" />
                Edit Details
              </Button>
            )
          )}
        </div>
      </div>

      {/* Status Alert for Pending */}
      {institution.status === "pending" && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="py-4">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-amber-900">
                  Application Under Review
                </h4>
                <p className="mt-1 text-sm text-amber-700">
                  Your institution registration is being reviewed by our team.
                  You&apos;ll be notified once it&apos;s approved.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Institution Header Card */}
      <Card className="overflow-hidden">
        <div className="bg-gradient-to-r from-primary/5 to-primary/10 px-6 py-8">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
            <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-2xl border-2 border-primary/20 bg-primary/10">
              <Building className="h-10 w-10 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-2 flex flex-wrap items-center gap-3">
                <h2 className="truncate text-2xl font-bold text-foreground">
                  {institution.name}
                </h2>
                <Badge className={`${statusConfig.className} gap-1.5 border`}>
                  <StatusIcon
                    className={`h-3.5 w-3.5 ${statusConfig.iconClass}`}
                  />
                  {statusConfig.label}
                </Badge>
              </div>
              <p className="capitalize text-muted-foreground">
                {institution.type ?? "Educational Institution"}
              </p>
            </div>
          </div>
        </div>

        <CardContent className="p-6">
          <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
            <div className="flex items-center gap-3 rounded-xl bg-muted/50 p-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <MapPin className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Location
                </p>
                <p className="truncate text-sm font-semibold capitalize">
                  {institution.city_main_name ??
                    mainCityOption?.name ??
                    institution.city_name ??
                    institution.city ??
                    "-"}
                  {", "}
                  {institution.state_main_name ??
                    mainStateOption?.name ??
                    institution.state_name ??
                    institution.state ??
                    "-"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-xl bg-muted/50 p-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Building className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Institution Type
                </p>
                <p className="text-sm font-semibold capitalize">
                  {institution.type ?? "-"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-xl bg-muted/50 p-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Calendar className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  ESTD Year
                </p>
                <p className="text-sm font-semibold">
                  {institution.year_established ?? "-"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-xl bg-muted/50 p-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Calendar className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Registered
                </p>
                <p className="text-sm font-semibold">
                  {formatDate(institution.created_at)}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stepper - centered, same design as super-admin InstitutionDetail */}
      <div className="flex justify-center px-4">
        <div className="flex w-full max-w-2xl items-center justify-between">
          {STEPS.map((step, index) => (
            <div key={step} className="flex flex-1 items-center">
              <div className="flex flex-col items-center">
                <button
                  type="button"
                  onClick={() => setCurrentStep(index + 1)}
                  className={`flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors ${
                    index + 1 <= currentStep
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-muted bg-background hover:border-primary/50"
                  }`}
                >
                  {index + 1 < currentStep ? (
                    <Check className="h-5 w-5" />
                  ) : (
                    index + 1
                  )}
                </button>
                <span className="mt-2 max-w-[80px] text-center text-xs">
                  {step}
                </span>
              </div>
              {index < STEPS.length - 1 && (
                <div
                  className={`mx-2 mb-[20px] h-0.5 flex-1 ${index + 1 < currentStep ? "bg-primary" : "bg-muted"}`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Form Content by Step */}
      <Card>
        <CardHeader className="border-b bg-muted/30">
          <CardTitle className="text-lg">{STEPS[currentStep - 1]}</CardTitle>
          <CardDescription>
            Step {currentStep} of {STEPS.length}: Complete information about
            your institution
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          {/* Step 1: Basic Info (same as InstitutionOnboarding) */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium">
                    Institution Name
                  </Label>
                  {isEditing ? (
                    <Input
                      id="name"
                      value={formData.name ?? ""}
                      onChange={e => updateField("name", e.target.value)}
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">{institution.name}</span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type" className="text-sm font-medium">
                    Institution Type
                  </Label>
                  {isEditing ? (
                    <Select
                      value={formData.type ?? ""}
                      onValueChange={v => updateField("type", v)}
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {INSTITUTION_TYPES.map(t => (
                          <SelectItem key={t.value} value={t.value}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium capitalize">
                        {institution.type ?? "Not specified"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    Year Established
                  </Label>
                  {isEditing ? (
                    <Input
                      type="number"
                      min={0}
                      value={formData.year_established ?? ""}
                      onWheel={e => e.currentTarget.blur()}
                      onKeyDown={e => {
                        if (["e", "E", "-", "+"].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onChange={e => {
                        const val = parseInt(e.target.value);
                        updateField(
                          "year_established",
                          isNaN(val) ? null : Math.max(0, val)
                        );
                      }}
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {institution.year_established ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4 md:col-span-2 lg:col-span-3">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Contact Person Name
                    </Label>
                    {isEditing ? (
                      <Input
                        value={formData.contact_person ?? ""}
                        onChange={e =>
                          updateField("contact_person", e.target.value)
                        }
                        placeholder="Enter contact person name"
                        className="h-11"
                      />
                    ) : (
                      <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                        <span className="font-medium">
                          {institution.contact_person ?? "Not provided"}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Designation</Label>
                    {isEditing ? (
                      <Select
                        value={formData.designation ?? ""}
                        onValueChange={v => updateField("designation", v)}
                      >
                        <SelectTrigger className="h-11">
                          <SelectValue
                            placeholder={
                              loadingDesignations
                                ? "Loading..."
                                : "Select designation"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {designationOptions.map(d => (
                            <SelectItem key={d.id} value={String(d.id)}>
                              {d.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                        <span className="font-medium">
                          {getDesignationLabel(institution.designation)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium">
                    Email Address
                  </Label>
                  {isEditing ? (
                    <Input
                      id="email"
                      type="email"
                      value={formData.email ?? ""}
                      onChange={e => updateField("email", e.target.value)}
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center gap-3 rounded-lg border bg-muted/50 px-4">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">
                        {institution.email ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm font-medium">
                    Phone Number
                  </Label>
                  {isEditing ? (
                    <Input
                      id="phone"
                      value={formData.phone ?? ""}
                      maxLength={10}
                      onChange={e => {
                        const value = e.target.value.replace(/\D/g, "");
                        updateField("phone", value);
                      }}
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center gap-3 rounded-lg border bg-muted/50 px-4">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">
                        {institution.phone ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2 md:col-span-2 lg:col-span-3">
                  <Label htmlFor="website" className="text-sm font-medium">
                    Website
                  </Label>
                  {isEditing ? (
                    <Input
                      id="website"
                      value={formData.website ?? ""}
                      onChange={e => updateField("website", e.target.value)}
                      placeholder="https://example.com"
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center gap-3 rounded-lg border bg-muted/50 px-4">
                      <Globe className="h-4 w-4 text-muted-foreground" />
                      {institution.website ? (
                        <a
                          href={institution.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="truncate font-medium text-primary hover:underline"
                        >
                          {institution.website}
                        </a>
                      ) : (
                        <span className="font-medium text-muted-foreground">
                          Not provided
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Main State</Label>
                  <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                    <span className="font-medium">
                      {institution.state_main_name ??
                        mainStateOption?.name ??
                        institution.state_main ??
                        institution.state_name ??
                        institution.state ??
                        "Not provided"}
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Main District</Label>
                  <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                    <span className="font-medium">
                      {institution.district_main_name ??
                        mainDistrictOption?.name ??
                        institution.district_main ??
                        institution.district_name ??
                        institution.district ??
                        "Not provided"}
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Main City</Label>
                  <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                    <span className="font-medium">
                      {institution.city_main_name ??
                        mainCityOption?.name ??
                        institution.city_main ??
                        institution.city_name ??
                        institution.city ??
                        "Not provided"}
                    </span>
                  </div>
                </div>
                {/* <div className="space-y-2">
                  <Label className="text-sm font-medium">Main Area</Label>
                  <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                    <span className="font-medium">
                      {mainAreaOption?.name ??
                        institution.area_main ??
                        "Not provided"}
                    </span>
                  </div>
                </div> */}
                <div className="space-y-2 md:col-span-2 lg:col-span-3">
                  <Label className="text-sm font-medium">
                    Institution Media (Images & Videos)
                  </Label>
                  <div className="grid grid-cols-3 gap-3 rounded-lg border bg-muted/20 p-4 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8">
                    {(() => {
                      const existing = institution.image ?? [];
                      const keptExistingCount =
                        existing.length - removedImageIndices.size;
                      const totalCount =
                        keptExistingCount + newImageFiles.length;
                      const canAdd =
                        isEditing && totalCount < MAX_INSTITUTION_IMAGES;

                      return (
                        <>
                          {/* Existing Media */}
                          {existing.map((img, originalIdx) => {
                            if (removedImageIndices.has(originalIdx)) {
                              return null;
                            }
                            const url =
                              typeof img === "string"
                                ? img
                                : (img as { url: string }).url;
                            const isVideo = isMediaVideo(url);
                            return (
                              <div
                                key={`existing-${url}`}
                                className="group relative aspect-square cursor-pointer overflow-hidden rounded-lg border border-background bg-muted shadow-sm transition-all hover:z-10 hover:scale-[1.05] hover:shadow-md"
                                onClick={() =>
                                  !isEditing && handleMediaClick(url)
                                }
                              >
                                {isVideo ? (
                                  <div className="h-full w-full">
                                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/10 transition-colors group-hover:bg-black/30">
                                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white shadow-md transition-transform group-hover:scale-110">
                                        <Play className="ml-0.5 h-4 w-4 fill-current" />
                                      </div>
                                    </div>
                                    <video
                                      src={url}
                                      className="h-full w-full object-cover"
                                    />
                                  </div>
                                ) : (
                                  <div className="relative h-full w-full">
                                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-300 group-hover:bg-black/20 group-hover:opacity-100">
                                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-primary shadow-md">
                                        <Eye className="h-4 w-4" />
                                      </div>
                                    </div>
                                    <img
                                      src={url}
                                      alt="Institution Media"
                                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                                    />
                                  </div>
                                )}

                                {isEditing && (
                                  <button
                                    type="button"
                                    onClick={e => {
                                      e.stopPropagation();
                                      setRemovedImageIndices(prev =>
                                        new Set(prev).add(originalIdx)
                                      );
                                    }}
                                    className="absolute right-1 top-1 z-20 flex h-6 w-6 items-center justify-center rounded-full bg-red-600 text-white shadow-lg transition-transform hover:scale-110"
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </div>
                            );
                          })}

                          {/* New Media Files */}
                          {newImageFiles.map((file, fileIndex) => {
                            const url = URL.createObjectURL(file);
                            const isVideo = file.type.startsWith("video/");
                            return (
                              <div
                                key={`new-${file.name}`}
                                className="group relative aspect-square cursor-pointer overflow-hidden rounded-lg border-2 border-primary/30 bg-muted shadow-md transition-all hover:z-10 hover:scale-[1.05]"
                              >
                                {isVideo ? (
                                  <video
                                    src={url}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <img
                                    src={url}
                                    alt="New Upload"
                                    className="h-full w-full object-cover"
                                  />
                                )}
                                <div className="absolute inset-0 bg-primary/5 transition-colors group-hover:bg-transparent" />
                                <button
                                  type="button"
                                  onClick={e => {
                                    e.stopPropagation();
                                    setNewImageFiles(prev =>
                                      prev.filter((_, i) => i !== fileIndex)
                                    );
                                  }}
                                  className="absolute right-1 top-1 z-20 flex h-6 w-6 items-center justify-center rounded-full bg-red-600 text-white shadow-lg transition-transform hover:scale-110"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                                <Badge className="absolute bottom-1 left-1 bg-primary px-1 text-[8px]">
                                  NEW
                                </Badge>
                              </div>
                            );
                          })}

                          {/* Upload Button */}
                          {canAdd && (
                            <label className="flex aspect-square cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-primary/20 bg-primary/5 transition-all hover:border-primary/40 hover:bg-primary/10">
                              <Upload className="h-6 w-6 text-primary/60" />
                              <span className="text-[10px] font-medium text-primary/70">
                                Add More
                              </span>
                              <input
                                type="file"
                                accept="image/*,video/*"
                                multiple
                                className="hidden"
                                onChange={e => {
                                  const files = Array.from(
                                    e.target.files ?? []
                                  );
                                  const remaining =
                                    MAX_INSTITUTION_IMAGES - totalCount;
                                  if (files.length) {
                                    setNewImageFiles(prev => [
                                      ...prev,
                                      ...files.slice(0, remaining),
                                    ]);
                                  }
                                  e.target.value = "";
                                }}
                              />
                            </label>
                          )}

                          {/* Empty State */}
                          {!isEditing && totalCount === 0 && (
                            <div className="col-span-full flex flex-col items-center justify-center gap-3 py-10 text-muted-foreground">
                              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                                <Upload className="h-8 w-8 opacity-40" />
                              </div>
                              <p className="text-sm font-medium">
                                No media uploaded
                              </p>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Address */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2 md:col-span-2">
                  <Label
                    htmlFor="address_line1"
                    className="text-sm font-medium"
                  >
                    Address Line 1
                  </Label>
                  {isEditing ? (
                    <Input
                      id="address_line1"
                      value={formData.address_line1 ?? ""}
                      onChange={e =>
                        updateField("address_line1", e.target.value)
                      }
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {institution.address_line1 ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label
                    htmlFor="address_line2"
                    className="text-sm font-medium"
                  >
                    Address Line 2
                  </Label>
                  {isEditing ? (
                    <Input
                      id="address_line2"
                      value={formData.address_line2 ?? ""}
                      onChange={e =>
                        updateField("address_line2", e.target.value)
                      }
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {institution.address_line2 ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="district" className="text-sm font-medium">
                    District
                  </Label>
                  {isEditing ? (
                    <Input
                      id="district"
                      value={formData.district ?? ""}
                      onChange={e => updateField("district", e.target.value)}
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {institution.district_name ??
                          districtOption?.name ??
                          institution.district ??
                          "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city" className="text-sm font-medium">
                    City
                  </Label>
                  {isEditing ? (
                    <Input
                      id="city"
                      value={formData.city ?? ""}
                      onChange={e => updateField("city", e.target.value)}
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {institution.city_name ??
                          cityOption?.name ??
                          institution.city ??
                          "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state" className="text-sm font-medium">
                    State
                  </Label>
                  {isEditing ? (
                    <Input
                      id="state"
                      value={formData.state ?? ""}
                      onChange={e => updateField("state", e.target.value)}
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {institution.state_name ??
                          stateOption?.name ??
                          institution.state ??
                          "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pincode" className="text-sm font-medium">
                    Pincode
                  </Label>
                  {isEditing ? (
                    <Input
                      id="pincode"
                      value={formData.pincode ?? ""}
                      maxLength={6}
                      onChange={e => {
                        const value = e.target.value.replace(/\D/g, "");
                        updateField("pincode", value);
                      }}
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {institution.pincode ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: School Details (Only if its applicable) */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    Board / University
                  </Label>
                  {isEditing ? (
                    <Select
                      value={formData.board ?? ""}
                      onValueChange={v => updateField("board", v)}
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Select board" />
                      </SelectTrigger>
                      <SelectContent>
                        {BOARDS.map(b => (
                          <SelectItem key={b.value} value={b.value}>
                            {b.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium capitalize">
                        {institution.board ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    Medium of Instruction
                  </Label>
                  <div className="flex min-h-[60px] flex-wrap gap-2 rounded-lg border bg-muted/50 p-4">
                    {(() => {
                      const mediumLabels = getMediumLabels(
                        formData.medium ?? institution.medium ?? null
                      );
                      return mediumLabels.length > 0 ? (
                        mediumLabels.map(label => (
                          <Badge
                            key={label}
                            variant="secondary"
                            className="border-0 bg-primary/10 px-3 py-1.5 text-primary"
                          >
                            {label}
                          </Badge>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          No medium selected
                        </p>
                      );
                    })()}
                  </div>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label className="text-sm font-medium">
                    Education Levels Offered
                  </Label>
                  {isEditing ? (
                    <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-4">
                      {INSTITUTION_LEVELS.map(level => {
                        const raw =
                          formData.selected_levels ??
                          institution?.selected_levels ??
                          [];
                        const valid = raw.filter(
                          (l): l is string =>
                            typeof l === "string" && l.trim().length > 0
                        );
                        const selected = valid.includes(level.value);
                        return (
                          <div
                            key={level.value}
                            className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 transition-colors ${
                              selected
                                ? "border-primary bg-primary text-primary-foreground"
                                : "hover:bg-muted"
                            }`}
                            onClick={() => toggleLevel(level.value)}
                          >
                            <CheckCircle
                              className={`h-4 w-4 ${selected ? "opacity-100" : "opacity-0"}`}
                            />
                            <span className="text-sm">{level.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex min-h-[60px] flex-wrap gap-2 rounded-lg border bg-muted/50 p-4">
                      {(() => {
                        const levels = (
                          institution.selected_levels ?? []
                        ).filter(
                          (l): l is string =>
                            typeof l === "string" && l.trim().length > 0
                        );
                        return levels.length > 0 ? (
                          levels.map(level => (
                            <Badge
                              key={level}
                              variant="secondary"
                              className="border-0 bg-primary/10 px-3 py-1.5 text-primary"
                            >
                              {INSTITUTION_LEVELS.find(l => l.value === level)
                                ?.label ?? level.replace(/_/g, " ")}
                            </Badge>
                          ))
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            No levels added
                          </p>
                        );
                      })()}
                    </div>
                  )}
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label className="text-sm font-medium">Hostel Gender</Label>
                  {isEditing ? (
                    <div className="mt-2 flex flex-wrap gap-3">
                      {BOARDING_GENDER.map(opt => {
                        const raw =
                          formData.boarding_gender ??
                          institution?.boarding_gender ??
                          "";
                        const currentArr =
                          typeof raw === "string" && raw
                            ? raw.split("/").filter(Boolean)
                            : [];
                        const selected = currentArr.includes(opt.value);
                        return (
                          <div
                            key={opt.value}
                            role="button"
                            tabIndex={0}
                            className={`flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2 transition-colors ${
                              selected
                                ? "border-primary bg-primary text-primary-foreground"
                                : "hover:bg-muted"
                            }`}
                            onClick={() => {
                              const next = selected
                                ? currentArr.filter(v => v !== opt.value)
                                : [...currentArr, opt.value];
                              updateField(
                                "boarding_gender",
                                next.length ? next.sort().join("/") : ""
                              );
                            }}
                            onKeyDown={e => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                const next = selected
                                  ? currentArr.filter(v => v !== opt.value)
                                  : [...currentArr, opt.value];
                                updateField(
                                  "boarding_gender",
                                  next.length ? next.sort().join("/") : ""
                                );
                              }
                            }}
                          >
                            <CheckCircle
                              className={`h-4 w-4 ${selected ? "opacity-100" : "opacity-0"}`}
                            />
                            <span className="text-sm">{opt.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium capitalize">
                        {(() => {
                          const raw = institution.boarding_gender ?? "";
                          if (!raw) {
                            return "Not provided";
                          }
                          const parts =
                            typeof raw === "string"
                              ? raw.split("/").filter(Boolean)
                              : [];
                          return parts.length > 0
                            ? parts
                                .map(
                                  p =>
                                    BOARDING_GENDER.find(
                                      g => g.value === p.trim()
                                    )?.label ?? p
                                )
                                .join(", ")
                            : "Not provided";
                        })()}
                      </span>
                    </div>
                  )}
                </div>
                {/* <div className="space-y-2 md:col-span-2">
                  <Label className="text-sm font-medium">
                    Educational Year & Capacity
                  </Label>
                  <p className="mb-2 text-xs text-muted-foreground">
                    Add one or more rows: educational year, total capacity, and
                    vacant/occupied count.
                  </p>
                  {isEditing ? (
                    <>
                      <div className="mb-3 grid gap-3 rounded-lg border bg-muted/30 p-3 md:grid-cols-4">
                        <Input
                          placeholder="e.g. 2025-2026"
                          value={educationalYearDraft.educational_year}
                          onChange={e =>
                            setEducationalYearDraft(prev => ({
                              ...prev,
                              educational_year: e.target.value,
                            }))
                          }
                        />
                        <Input
                          type="number"
                          min={0}
                          placeholder="Total capacity"
                          value={educationalYearDraft.total_capacity}
                          onChange={e => {
                            const val = e.target.value;
                            const capacity = Number(val);
                            const occupied = Number(
                              educationalYearDraft.occupied
                            );
                            setEducationalYearDraft(prev => ({
                              ...prev,
                              total_capacity: val,
                              vacant:
                                !isNaN(capacity) && !isNaN(occupied)
                                  ? String(Math.max(0, capacity - occupied))
                                  : prev.vacant,
                            }));
                          }}
                          onWheel={e => e.currentTarget.blur()}
                          onKeyDown={e => {
                            if (["e", "E", "-", "+"].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                        />
                        <Input
                          type="number"
                          min={0}
                          placeholder="Occupied"
                          value={educationalYearDraft.occupied}
                          onChange={e => {
                            const val = e.target.value;
                            const occupied = Number(val);
                            const capacity = Number(
                              educationalYearDraft.total_capacity
                            );
                            setEducationalYearDraft(prev => ({
                              ...prev,
                              occupied: val,
                              vacant:
                                !isNaN(capacity) && !isNaN(occupied)
                                  ? String(Math.max(0, capacity - occupied))
                                  : prev.vacant,
                            }));
                          }}
                          onWheel={e => e.currentTarget.blur()}
                          onKeyDown={e => {
                            if (["e", "E", "-", "+"].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                        />
                        <Input
                          type="number"
                          min={0}
                          placeholder="Vacant"
                          value={educationalYearDraft.vacant}
                          onChange={e =>
                            setEducationalYearDraft(prev => ({
                              ...prev,
                              vacant: e.target.value,
                            }))
                          }
                          onWheel={e => e.currentTarget.blur()}
                          onKeyDown={e => {
                            if (["e", "E", "-", "+"].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                        />
                        <Button
                          type="button"
                          variant="secondary"
                          className="gap-1"
                          onClick={() => {
                            const year =
                              educationalYearDraft.educational_year.trim();
                            const capacity = Number(
                              educationalYearDraft.total_capacity
                            );
                            const vacant = Number(educationalYearDraft.vacant);
                            const occupied = Number(
                              educationalYearDraft.occupied
                            );
                            if (!year) {
                              return;
                            }
                            const current =
                              formData.educational_year ??
                              institution?.educational_year ??
                              [];
                            updateField("educational_year", [
                              ...current,
                              {
                                educational_year: year,
                                total_capacity: Number.isNaN(capacity)
                                  ? 0
                                  : capacity,
                                vacant: Number.isNaN(vacant) ? 0 : vacant,
                                occupied: Number.isNaN(occupied) ? 0 : occupied,
                              },
                            ]);
                            setEducationalYearDraft({
                              educational_year: "",
                              total_capacity: "",
                              vacant: "",
                              occupied: "",
                            });
                          }}
                        >
                          <Plus className="h-4 w-4" />
                          Add
                        </Button>
                      </div>
                      {(
                        formData.educational_year ??
                        institution?.educational_year ??
                        []
                      ).length > 0 && (
                        <div className="overflow-hidden rounded-lg border bg-background">
                          <div className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-3 border-b bg-muted/50 px-3 py-2 text-xs font-medium text-muted-foreground">
                            <div>Educational Year</div>
                            <div>Total Capacity</div>
                            <div>Occupied</div>
                            <div>Vacant</div>
                            <div className="w-10" />
                          </div>
                          {(
                            formData.educational_year ??
                            institution?.educational_year ??
                            []
                          ).map(
                            (
                              row: {
                                educational_year: string;
                                total_capacity: number;
                                vacant: number;
                                occupied: number;
                              },
                              index: number
                            ) => (
                              <div
                                key={`${row.educational_year}`}
                                className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] items-center gap-3 border-b px-3 py-2 text-sm last:border-b-0"
                              >
                                <div className="font-medium">
                                  {row.educational_year}
                                </div>
                                <div>{row.total_capacity}</div>
                                <div>{row.occupied}</div>
                                <div>{row.vacant}</div>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  className="h-9 w-10 text-destructive hover:bg-destructive/10"
                                  onClick={() => {
                                    const current =
                                      formData.educational_year ??
                                      institution?.educational_year ??
                                      [];
                                    updateField(
                                      "educational_year",
                                      current.filter(
                                        (_: unknown, i: number) => i !== index
                                      )
                                    );
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            )
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="overflow-hidden rounded-lg border bg-muted/30">
                      {(institution.educational_year ?? []).length > 0 ? (
                        <>
                          <div className="grid grid-cols-[1fr_1fr_1fr_1fr] gap-3 border-b bg-muted/50 px-3 py-2 text-xs font-medium text-muted-foreground">
                            <div>Educational Year</div>
                            <div>Total Capacity</div>
                            <div>Occupied</div>
                            <div>Vacant</div>
                          </div>
                          {(institution.educational_year ?? []).map(
                            (row: {
                              educational_year: string;
                              total_capacity: number;
                              vacant: number;
                              occupied: number;
                            }) => (
                              <div
                                key={`${row.educational_year}`}
                                className="grid grid-cols-[1fr_1fr_1fr_1fr] items-center gap-3 border-b px-3 py-2 text-sm last:border-b-0"
                              >
                                <div className="font-medium">
                                  {row.educational_year}
                                </div>
                                <div>{row.total_capacity}</div>
                                <div>{row.occupied}</div>
                                <div>{row.vacant}</div>
                              </div>
                            )
                          )}
                        </>
                      ) : (
                        <div className="px-4 py-3 text-sm text-muted-foreground">
                          No entries added
                        </div>
                      )}
                    </div>
                  )}
                </div> */}
                <div className="space-y-2 md:col-span-2">
                  <Label className="text-sm font-medium">
                    Registration Number
                  </Label>
                  {isEditing ? (
                    <Input
                      value={formData.recognition_number ?? ""}
                      onChange={e =>
                        updateField("recognition_number", e.target.value)
                      }
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {institution.recognition_number ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4 md:col-span-2">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Admission Link
                    </Label>
                    <div className="flex h-11 items-center gap-3 rounded-lg border bg-muted/50 px-4">
                      <Globe className="h-4 w-4 text-muted-foreground" />
                      {institution.admission_link ? (
                        <a
                          href={
                            institution.admission_link.startsWith("http")
                              ? institution.admission_link
                              : `https://${institution.admission_link}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="truncate font-medium text-primary hover:underline"
                        >
                          {institution.admission_link}
                        </a>
                      ) : (
                        <span className="font-medium text-muted-foreground">
                          Not provided
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Brochure</Label>
                    <div className="flex h-11 items-center gap-3 rounded-lg border bg-muted/50 px-4">
                      <Upload className="h-4 w-4 text-muted-foreground" />
                      {institution.brochure ? (
                        <a
                          href={institution.brochure}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="truncate font-medium text-primary hover:underline"
                        >
                          View Brochure
                        </a>
                      ) : (
                        <span className="font-medium text-muted-foreground">
                          Not uploaded
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Capacity & Facilities */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-3 rounded-lg border bg-muted/20 p-4">
                  <h4 className="text-sm font-semibold">School Capacity</h4>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Total Students (In School)
                    </Label>
                    {isEditing ? (
                      <Input
                        type="number"
                        value={formData.total_students_in_school ?? ""}
                        min={0}
                        onWheel={e => e.currentTarget.blur()}
                        onKeyDown={e => {
                          if (["e", "E", "-", "+"].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onChange={e => {
                          const val = parseInt(e.target.value);
                          updateField(
                            "total_students_in_school",
                            isNaN(val) ? null : Math.max(0, val)
                          );
                        }}
                        className="h-11"
                      />
                    ) : (
                      <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                        <span className="font-medium">
                          {(
                            institution.total_students_in_school ??
                            institution.total_students
                          )?.toLocaleString() ?? "—"}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Total Classroom (In School)
                    </Label>
                    {isEditing ? (
                      <Input
                        type="number"
                        value={formData.total_classroom_in_school ?? ""}
                        min={0}
                        onWheel={e => e.currentTarget.blur()}
                        onKeyDown={e => {
                          if (["e", "E", "-", "+"].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onChange={e => {
                          const val = parseInt(e.target.value);
                          updateField(
                            "total_classroom_in_school",
                            isNaN(val) ? null : Math.max(0, val)
                          );
                        }}
                        className="h-11"
                      />
                    ) : (
                      <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                        <span className="font-medium">
                          {(
                            institution.total_classroom_in_school ??
                            institution.classrooms
                          )?.toLocaleString() ?? "—"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-3 rounded-lg border bg-muted/20 p-4">
                  <h4 className="text-sm font-semibold">Hostel Capacity</h4>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Total Students (In Hostel)
                    </Label>
                    {isEditing ? (
                      <Input
                        type="number"
                        value={formData.total_students_in_hostel ?? ""}
                        min={0}
                        onWheel={e => e.currentTarget.blur()}
                        onKeyDown={e => {
                          if (["e", "E", "-", "+"].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onChange={e => {
                          const val = parseInt(e.target.value);
                          updateField(
                            "total_students_in_hostel",
                            isNaN(val) ? null : Math.max(0, val)
                          );
                        }}
                        className="h-11"
                      />
                    ) : (
                      <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                        <span className="font-medium">
                          {institution.total_students_in_hostel?.toLocaleString() ??
                            "—"}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Total Rooms (In Hostel)
                    </Label>
                    {isEditing ? (
                      <Input
                        type="number"
                        value={formData.total_rooms_in_hostel ?? ""}
                        min={0}
                        onWheel={e => e.currentTarget.blur()}
                        onKeyDown={e => {
                          if (["e", "E", "-", "+"].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onChange={e => {
                          const val = parseInt(e.target.value);
                          updateField(
                            "total_rooms_in_hostel",
                            isNaN(val) ? null : Math.max(0, val)
                          );
                        }}
                        className="h-11"
                      />
                    ) : (
                      <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                        <span className="font-medium">
                          {institution.total_rooms_in_hostel?.toLocaleString() ??
                            "—"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Campus Area</Label>
                {isEditing ? (
                  <Input
                    type="number"
                    value={formData.campus_area ?? ""}
                    min={0}
                    onWheel={e => e.currentTarget.blur()}
                    onKeyDown={e => {
                      if (["e", "E", "-", "+"].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onChange={e => {
                      const val = parseInt(e.target.value);
                      updateField(
                        "campus_area",
                        isNaN(val) ? null : Math.max(0, val)
                      );
                    }}
                    className="h-11"
                  />
                ) : (
                  <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                    <span className="font-medium">
                      {institution.campus_area
                        ? String(institution.campus_area)
                        : "Not provided"}
                    </span>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Facilities Available
                </Label>
                {isEditing ? (
                  <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-4">
                    {loadingFacilities ? (
                      <div className="col-span-full flex items-center gap-2 rounded-lg border px-3 py-4 text-sm text-muted-foreground">
                        <CustomLoader size="xs" />
                        Loading facilities...
                      </div>
                    ) : facilityOptions.length === 0 ? (
                      <div className="col-span-full rounded-lg border px-3 py-4 text-sm text-muted-foreground">
                        No facilities available in Facilities Master.
                      </div>
                    ) : (
                      facilityOptions.map(facility => {
                        const raw =
                          formData.selected_facilities ??
                          institution?.selected_facilities ??
                          institution?.facilities ??
                          [];
                        const valid = normalizeFacilityIds(raw);
                        const selected = valid.includes(facility.id);
                        return (
                          <div
                            key={facility.id}
                            className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 transition-colors ${
                              selected
                                ? "border-primary bg-primary text-primary-foreground"
                                : "hover:bg-muted"
                            }`}
                            onClick={() => toggleFacility(facility.id)}
                          >
                            <CheckCircle
                              className={`h-4 w-4 ${selected ? "opacity-100" : "opacity-0"}`}
                            />
                            <span className="text-sm">{facility.name}</span>
                          </div>
                        );
                      })
                    )}
                  </div>
                ) : (
                  <div className="flex min-h-[60px] flex-wrap gap-2 rounded-lg border bg-muted/50 p-4">
                    {(() => {
                      const facs = normalizeFacilityIds(
                        institution.selected_facilities ??
                          institution.facilities ??
                          []
                      );
                      return facs.length > 0 ? (
                        facs.map((fac: string) => (
                          <Badge
                            key={fac}
                            variant="secondary"
                            className="border-0 bg-primary/10 px-3 py-1.5 text-primary"
                          >
                            {getFacilityLabel(fac)}
                          </Badge>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          No facilities added
                        </p>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 5: Trustee / Management Information */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="trustee_name" className="text-sm font-medium">
                    Trustee / Principal Name
                  </Label>
                  {isEditing ? (
                    <Input
                      id="trustee_name"
                      value={formData.trustee_name ?? ""}
                      onChange={e =>
                        updateField("trustee_name", e.target.value)
                      }
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center gap-3 rounded-lg border bg-muted/50 px-4">
                      <Shield className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">
                        {institution.trustee_name ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Designation</Label>
                  {isEditing ? (
                    <Select
                      value={formData.trustee_designation ?? ""}
                      onValueChange={v => updateField("trustee_designation", v)}
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue
                          placeholder={
                            loadingDesignations
                              ? "Loading..."
                              : "Select designation"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {designationOptions.map(d => (
                          <SelectItem key={d.id} value={String(d.id)}>
                            {d.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {getDesignationLabel(institution.trustee_designation)}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="trustee_contact"
                    className="text-sm font-medium"
                  >
                    Trustee Phone
                  </Label>
                  {isEditing ? (
                    <Input
                      id="trustee_contact"
                      value={formData.trustee_contact ?? ""}
                      maxLength={10}
                      onChange={e => {
                        const value = e.target.value.replace(/\D/g, "");
                        updateField("trustee_contact", value);
                      }}
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center gap-3 rounded-lg border bg-muted/50 px-4">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">
                        {institution.trustee_contact ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Trustee Email</Label>
                  {isEditing ? (
                    <Input
                      type="email"
                      value={formData.trustee_email ?? ""}
                      onChange={e =>
                        updateField("trustee_email", e.target.value)
                      }
                      placeholder="Enter email"
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center gap-3 rounded-lg border bg-muted/50 px-4">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">
                        {institution.trustee_email ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    Number of Trustees
                  </Label>
                  {isEditing ? (
                    <Input
                      type="number"
                      value={formData.trustee_count ?? ""}
                      min={0}
                      onWheel={e => e.currentTarget.blur()}
                      onKeyDown={e => {
                        if (["e", "E", "-", "+"].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onChange={e => {
                        const val = parseInt(e.target.value);
                        updateField(
                          "trustee_count",
                          isNaN(val) ? null : Math.max(0, val)
                        );
                      }}
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {institution.trustee_count?.toLocaleString() ?? "—"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    Trust Registration Number
                  </Label>
                  {isEditing ? (
                    <Input
                      value={formData.trust_registration ?? ""}
                      onChange={e =>
                        updateField("trust_registration", e.target.value)
                      }
                      placeholder="Enter registration number"
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {institution.trust_registration ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label className="text-sm font-medium">
                    Names of Other Trustees
                  </Label>
                  {isEditing ? (
                    <Textarea
                      value={formData.other_trustees ?? ""}
                      onChange={e =>
                        updateField("other_trustees", e.target.value)
                      }
                      placeholder="Enter names separated by comma"
                      rows={2}
                      className="resize-none"
                    />
                  ) : (
                    <div className="min-h-[60px] rounded-lg border bg-muted/50 px-4 py-3">
                      <span className="whitespace-pre-wrap font-medium">
                        {institution.other_trustees ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stepper Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={handleBack}
          disabled={currentStep === 1}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        {currentStep < STEPS.length ? (
          <Button onClick={handleNext}>
            Next
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        ) : (
          <div />
        )}
      </div>

      {/* Media Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-[95vw] border-none bg-transparent p-0 shadow-none outline-none focus-visible:ring-0 sm:max-w-[90vw] md:max-w-4xl lg:max-w-5xl [&>button]:hidden">
          <div className="relative h-full w-full overflow-hidden rounded-2xl bg-zinc-950/50 backdrop-blur-md">
            {/* Custom Close Button */}
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute right-4 top-4 z-[60] flex h-10 w-10 items-center justify-center rounded-full bg-primary text-white transition-all hover:scale-110 hover:bg-red-700/80"
            >
              <X className="h-6 w-6" />
            </button>
            {selectedMedia?.type === "video" ? (
              <video
                src={selectedMedia?.url}
                className="max-h-[85vh] w-full"
                controls
                autoPlay
              />
            ) : (
              <img
                src={selectedMedia?.url}
                alt="View Media"
                className="max-h-[85vh] w-full object-contain"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
